/**
 * SceneWorld — a fixed illustrated place.
 *
 * PorchLight is a game, not an image viewer. Each location loads one world
 * with a fixed pixel size. The browser never rescales that world; the camera
 * crops into it as the viewport changes.
 *
 * Desktop / Tablet / Mobile are separate SceneWorlds for the same place.
 * Tablet reuses Mobile master artwork with independent prop transforms.
 */

import {
  BACKYARD_MASTER_DESKTOP_ASPECT,
  BACKYARD_MASTER_DESKTOP_SRC,
  BACKYARD_MASTER_MOBILE_ASPECT,
  BACKYARD_MASTER_MOBILE_SRC,
  BACKYARD_PLAY_AREA,
  RALEIGH_MASTER_DESKTOP_ASPECT,
  RALEIGH_MASTER_DESKTOP_SRC,
  RALEIGH_MASTER_MOBILE_ASPECT,
  RALEIGH_MASTER_MOBILE_SRC,
  RALEIGH_PLAY_AREA,
  SPANISH_FORK_MASTER_DESKTOP_ASPECT,
  SPANISH_FORK_MASTER_DESKTOP_SRC,
  SPANISH_FORK_MASTER_MOBILE_ASPECT,
  SPANISH_FORK_MASTER_MOBILE_SRC,
  SPANISH_FORK_PLAY_AREA,
  artworkPlateFor,
  usesMobileCoverCamera,
  type ArtworkPlayArea,
  type BackyardPresentationMode,
} from '@/world-prototype/camera/backyardPresentation';
import type { PlayAreaRect } from '@/world-prototype/camera/responsiveExploreZoom';
import { computePlayAreaFit } from '@/world-prototype/camera/responsiveExploreZoom';

/** Hubert / Holly Springs source master dimensions (1672×941). */
export const MASTER_PIXEL_SIZE = { width: 1672, height: 941 } as const;

/** Spanish Fork landscape master dimensions (1024×576, 16:9). */
export const SPANISH_FORK_MASTER_PIXEL_SIZE = {
  width: 1024,
  height: 576,
} as const;

/**
 * Freeze world CSS size from the desktop design reference so fullscreen
 * framing matches the approved composition (former 1.08× of 1600×900).
 */
export const WORLD_LAYOUT_REFERENCE = {
  viewportWidth: 1600,
  viewportHeight: 900,
  sizeFactor: 1.08,
} as const;

export type BackyardLocationId = 'hubert' | 'raleigh' | 'spanish-fork';

export type SceneWorldId =
  | 'hubert-desktop'
  | 'hubert-tablet'
  | 'hubert-mobile'
  | 'raleigh-desktop'
  | 'raleigh-tablet'
  | 'raleigh-mobile'
  | 'spanish-fork-desktop'
  | 'spanish-fork-tablet'
  | 'spanish-fork-mobile';

export type WorldCameraPolicy = 'desktop-play-area' | 'mobile-cover';

/**
 * Fixed world loaded into the playfield. Artwork pixel box never changes
 * after load (only swaps when selecting a different SceneWorld).
 */
export interface SceneWorld {
  id: SceneWorldId;
  /** Logical location this world represents. */
  locationId: BackyardLocationId;
  presentation: BackyardPresentationMode;
  widthPx: number;
  heightPx: number;
  aspectRatio: number;
  artworkSrc: string;
  artworkAlt: string;
  /** Fenced yard in normalized 0–1 world space. */
  playArea: PlayAreaRect;
  cameraPolicy: WorldCameraPolicy;
  /**
   * Desktop only: locked camera zoom for the design reference.
   * Resize crops the viewport; zoom does not shrink with the browser.
   */
  designZoom: number | null;
}

export function isBackyardLocationId(
  id: string | null | undefined,
): id is BackyardLocationId {
  return id === 'hubert' || id === 'raleigh' || id === 'spanish-fork';
}

export function resolveFixedWorldSize(aspectRatio: number): {
  widthPx: number;
  heightPx: number;
} {
  const aspect = Math.max(0.01, aspectRatio);
  const widthPx = Math.round(
    WORLD_LAYOUT_REFERENCE.viewportWidth * WORLD_LAYOUT_REFERENCE.sizeFactor,
  );
  const heightPx = Math.round(widthPx / aspect);
  return { widthPx, heightPx };
}

export function playAreaAsRect(area: ArtworkPlayArea): PlayAreaRect {
  return {
    left: area.left,
    top: area.top,
    right: area.right,
    bottom: area.bottom,
  };
}

/** Build the SceneWorld for a backyard location + layout profile. */
export function resolveBackyardSceneWorld(
  locationId: BackyardLocationId,
  presentation: BackyardPresentationMode,
): SceneWorld {
  const plate = artworkPlateFor(presentation);
  const isMobileArt = plate === 'mobile';
  const coverCamera = usesMobileCoverCamera(presentation);

  let aspectRatio: number;
  let artworkSrc: string;
  let artworkAlt: string;
  let playSource: Readonly<Record<BackyardPresentationMode, ArtworkPlayArea>>;

  if (locationId === 'spanish-fork') {
    aspectRatio = isMobileArt
      ? SPANISH_FORK_MASTER_MOBILE_ASPECT
      : SPANISH_FORK_MASTER_DESKTOP_ASPECT;
    artworkSrc = isMobileArt
      ? SPANISH_FORK_MASTER_MOBILE_SRC
      : SPANISH_FORK_MASTER_DESKTOP_SRC;
    artworkAlt =
      presentation === 'tablet'
        ? 'Spanish Fork backyard — tablet world'
        : isMobileArt
          ? 'Spanish Fork backyard — mobile world'
          : 'Spanish Fork backyard — desktop world';
    playSource = SPANISH_FORK_PLAY_AREA;
  } else if (locationId === 'raleigh') {
    aspectRatio = isMobileArt
      ? RALEIGH_MASTER_MOBILE_ASPECT
      : RALEIGH_MASTER_DESKTOP_ASPECT;
    artworkSrc = isMobileArt
      ? RALEIGH_MASTER_MOBILE_SRC
      : RALEIGH_MASTER_DESKTOP_SRC;
    artworkAlt =
      presentation === 'tablet'
        ? 'Holly Springs backyard — tablet world'
        : isMobileArt
          ? 'Holly Springs backyard — mobile world'
          : 'Holly Springs backyard — desktop world';
    playSource = RALEIGH_PLAY_AREA;
  } else {
    aspectRatio = isMobileArt
      ? BACKYARD_MASTER_MOBILE_ASPECT
      : BACKYARD_MASTER_DESKTOP_ASPECT;
    artworkSrc = isMobileArt
      ? BACKYARD_MASTER_MOBILE_SRC
      : BACKYARD_MASTER_DESKTOP_SRC;
    artworkAlt =
      presentation === 'tablet'
        ? 'Hubert backyard — tablet world'
        : isMobileArt
          ? 'Hubert backyard — mobile world'
          : 'Hubert backyard — desktop world';
    playSource = BACKYARD_PLAY_AREA;
  }

  const { widthPx, heightPx } = resolveFixedWorldSize(aspectRatio);

  return {
    id: `${locationId}-${presentation}`,
    locationId,
    presentation,
    widthPx,
    heightPx,
    aspectRatio,
    artworkSrc,
    artworkAlt,
    playArea: playAreaAsRect(playSource[presentation]),
    cameraPolicy: coverCamera ? 'mobile-cover' : 'desktop-play-area',
    designZoom: coverCamera
      ? null
      : resolveDesktopDesignZoom(
          widthPx,
          heightPx,
          playAreaAsRect(playSource.desktop),
        ),
  };
}

/**
 * Locked desktop zoom at the layout reference (play-area contain, cover floor).
 * Does not depend on the live browser size.
 */
export function resolveDesktopDesignZoom(
  widthPx: number,
  heightPx: number,
  playArea: PlayAreaRect,
): number {
  const { worldWidth, worldHeight } = worldFactorsForViewport(
    { widthPx, heightPx },
    WORLD_LAYOUT_REFERENCE.viewportWidth,
    WORLD_LAYOUT_REFERENCE.viewportHeight,
  );
  const fitted = computePlayAreaFit(worldWidth, worldHeight, playArea, 0);
  const coverMin = Math.max(1 / worldWidth, 1 / worldHeight);
  return Math.max(fitted.zoom, coverMin);
}

/** Viewport-relative factors for pan-extent math (not CSS sizing). */
export function worldFactorsForViewport(
  world: Pick<SceneWorld, 'widthPx' | 'heightPx'>,
  viewportWidth: number,
  viewportHeight: number,
): { worldWidth: number; worldHeight: number } {
  return {
    worldWidth: world.widthPx / Math.max(1, viewportWidth),
    worldHeight: world.heightPx / Math.max(1, viewportHeight),
  };
}
